import {useRender} from "@base-ui/react/use-render";
import {type VariantProps, cva} from "class-variance-authority";
import * as React from "react";

import {cn} from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-start rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 text-left [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline: "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        warning: "bg-amber-200 text-amber-800 border-amber-200 [a&]:hover:bg-amber-600",
        /** Muted orange — cancellations / “stopped” states (distinct from amber `warning`). */
        orange: "border-orange-200 bg-orange-200 text-orange-900 [a&]:hover:bg-orange-300",
        info: "bg-sky-200 text-sky-800 border-sky-200 [a&]:hover:bg-sky-600",
        success: "bg-green-200 text-green-800 border-green-200 [a&]:hover:bg-green-600",
        error: "bg-red-200 text-red-800 border-red-200 [a&]:hover:bg-red-600",
        danger: "bg-rose-200 text-rose-800 border-rose-200 [a&]:hover:bg-rose-600",
        disabled: "text-muted-foreground bg-muted [a&]:hover:bg-muted/90",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type BadgeProps = React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    render?: React.ReactElement;
  };

function Badge({className, variant, render, ...props}: BadgeProps) {
  const badgeProps = {
    "data-slot": "badge",
    className: cn(badgeVariants({variant}), className),
    ...props,
  };

  if (render) {
    return useRender({render, props: badgeProps});
  }

  return <span {...badgeProps} />;
}

export {Badge, badgeVariants};
export type {BadgeProps};
